import { Component, Inject, inject, Injector, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslationModule } from '../../../i18n/translation-module';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AddCampaignDialogComponent } from './add-campaign-dialog/add-campaign-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { AssociationCardComponent, CommonAssociationCardContentComponent, TagsComponent } from '../association-card';
import { MatCardModule } from '@angular/material/card';
import { CONTACT_PROFILE_CARD_DATA_TOKEN, CrmDependencies, CrmInjectionToken } from '../../../tokens-and-interfaces';
import { BehaviorSubject, Observable } from 'rxjs';
import { CampaignService, RecipientCampaignService, RecipientCampaignStruct, Statuses } from '@vendasta/campaigns';
import { map, switchMap, take } from 'rxjs/operators';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
export interface CampaignCardData {
  id: string;
  title: string;
  status: string;
  link: string;
}
@Component({
  selector: 'crm-campaigns-card',
  standalone: true,
  imports: [
    CommonModule,
    TranslationModule,
    MatExpansionModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    AssociationCardComponent,
    CommonAssociationCardContentComponent,
    MatCardModule,
    TagsComponent,
    GalaxyAvatarModule,
    MatMenuModule,
    MatTooltipModule,
    GalaxyBadgeModule,
    RouterLink,
  ],
  templateUrl: './contact-campaigns-card.component.html',
  styleUrls: ['./contact-campaigns-card.component.scss', '../../../shared-styles/association-panel-shared.scss'],
})
export class ContactCampaignsCardComponent {
  private readonly injector = inject(Injector);
  data = inject(CONTACT_PROFILE_CARD_DATA_TOKEN);
  campaigns$$ = new BehaviorSubject<CampaignCardData[]>([] as CampaignCardData[]);
  campaigns$ = this.campaigns$$.asObservable();
  useBusinessCenterCampaignLink: Signal<boolean | undefined>;

  constructor(
    private dialog: MatDialog,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly campaignService: CampaignService,
    private readonly recipientCampaignService: RecipientCampaignService,
    private router: Router,
  ) {
    this.campaigns$ = this.listRecipientCampaigns(this.data.id);
    this.useBusinessCenterCampaignLink = toSignal(
      config.parentNamespace$.pipe(
        map((parentNamespace) => {
          return parentNamespace !== '';
        }),
      ),
    );
  }

  addCampaigns() {
    this.dialog.open(AddCampaignDialogComponent, {
      width: '500px',
      data: {
        id: this.data.id,
      },
    });
  }

  listRecipientCampaigns(contactId: string): Observable<CampaignCardData[]> {
    const recipientCampaigns$ = this.recipientCampaignService.list(contactId).pipe(
      map((recipientCampaigns: RecipientCampaignStruct[]) => {
        return recipientCampaigns.map((recipientCampaign) => recipientCampaign.campaignId);
      }),
    );

    return recipientCampaigns$.pipe(
      switchMap((campaignIds) => {
        return this.campaignService.getMulti(campaignIds).pipe(
          map((campaigns) => {
            return campaigns.map((campaign) => {
              let statusAsString = Statuses[campaign.status];
              switch (campaign.status) {
                case Statuses.STATUSES_DRAFT:
                  statusAsString = 'Draft';
                  break;
                case Statuses.STATUSES_PUBLISHED:
                  statusAsString = 'Published';
                  break;
                case Statuses.STATUSES_ARCHIVED:
                  statusAsString = 'Archived';
                  break;
                case Statuses.STATUSES_ACTIVE:
                  statusAsString = 'Active';
                  break;
                case Statuses.STATUSES_UNSPECIFIED:
                  statusAsString = 'Unspecified';
                  break;
              }
              const campaignLink = this.getCampaignLink(campaign.campaignId, campaign.partnerId);
              return {
                id: campaign.campaignId,
                title: campaign.name,
                status: statusAsString,
                link: campaignLink,
              } as CampaignCardData;
            });
          }),
        );
      }),
    );
  }

  getCampaignLink(campaignId: string, namespace: string): string {
    if (this.useBusinessCenterCampaignLink()) {
      return `/account/location/${namespace}/campaigns/${campaignId}/details`;
    } else {
      return `/marketing/campaign/details/${campaignId}`;
    }
  }

  viewAll() {
    if (this.useBusinessCenterCampaignLink()) {
      this.config.namespace$.pipe(take(1)).subscribe((namespace) => {
        this.router.navigate([`/account/location/${namespace}/campaigns/list`]);
      });
    }
    this.router.navigate(['/marketing/campaigns/all']);
  }
}
